import { ReactElement } from "react";
import { create } from "zustand";

// project imports
import config, { MenuOrientation, ThemeDirection, ThemeMode } from "config";

// types
import { CustomizationProps, I18n } from "types/config";

// ── localStorage helpers ───────────────────────────────────────────────────────
const STORAGE_KEY = "mantis-react-ts-config";

const readStorage = (): Partial<CustomizationProps> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeStorage = (state: CustomizationProps) => {
  const {
    fontFamily,
    i18n,
    menuOrientation,
    miniDrawer,
    container,
    mode,
    presetColor,
    themeDirection,
  } = state;
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      fontFamily,
      i18n,
      menuOrientation,
      miniDrawer,
      container,
      mode,
      presetColor,
      themeDirection,
    }),
  );
};

// ── Zustand store ─────────────────────────────────────────────────────────────
const useConfigStore = create<CustomizationProps>((set) => ({
  ...config,
  ...readStorage(),
  onChangeContainer: (container: boolean) => set({ container }),
  onChangeLocalization: (i18n: I18n) => set({ i18n }),
  onChangeMode: (mode: ThemeMode) => set({ mode }),
  onChangeDirection: (themeDirection: ThemeDirection) =>
    set({ themeDirection }),
  onChangeMiniDrawer: (miniDrawer: boolean) =>
    set({ menuOrientation: MenuOrientation.VERTICAL, miniDrawer }),
  onChangeMenuOrientation: (menuOrientation: MenuOrientation) =>
    set({
      menuOrientation,
      ...(menuOrientation === MenuOrientation.VERTICAL && {
        miniDrawer: false,
      }),
    }),
}));

useConfigStore.subscribe(writeStorage);

// ── Thin wrappers for backward compat ─────────────────────────────────────────
// ConfigProvider is a no-op — Zustand is global, no React tree wrapping needed.
type ConfigProviderProps = { children: ReactElement };
function ConfigProvider({ children }: ConfigProviderProps) {
  return children;
}

export { ConfigProvider, useConfigStore };
